import {
  AloftArea,
  AloftPrice,
  CraftsmanCostProps,
  getCraftsmanCost,
  isUndefined,
  ORDER_FEATURES,
} from '@aloft/toolkit-shared';
import { createSelector } from '@reduxjs/toolkit';

import { selectCompSequence } from '../orderSelectors.ts';
import { selectImprovementEntityIds, selectImprovementsEntities } from '../propertyDataSelectors/improvementsSelectors.ts';
import { selectPropertyIdentificationEntities } from '../propertyDataSelectors/propertyIdentificationSelectors.ts';
import { createPriceSelector } from '../utils/createPriceSelector.ts';
import { selectCostProps } from './selectCostProps.ts';

const calculateTotalBasementAreaCost = (
  costProps: CraftsmanCostProps,
  improvements: ReturnType<typeof selectImprovementsEntities>,
  propertyIdentification: ReturnType<typeof selectPropertyIdentificationEntities>,
  id: string,
) => {
  const improvementsForTransaction = improvements[id];
  const identification = propertyIdentification[id];

  if (!improvementsForTransaction || !identification) return undefined;

  const {
    basement,
    craftsmanQuality: quality,
    propertyType = 'singleFamilyResidence',
  } = improvementsForTransaction;
  const {
    address: { PostalCode: zipCode },
  } = identification;

  if (isUndefined(quality) || (quality * 10) % 1 !== 0 || quality < 1 || isUndefined(zipCode) || !ORDER_FEATURES.basement.hasFeature(improvementsForTransaction)) {
    return undefined;
  }

  const calculator = getCraftsmanCost(costProps);

  const totalArea =
    basement && basement.totalArea ? AloftArea.schema.parse(basement.totalArea) : AloftArea.zero;

  return calculator.getTotalBasementAreaCost({
    totalArea,
    quality,
    propertyType,
    zipCode,
  });
};

const getTotalOrBreakdownBasementAreaCost = (
  costProps: CraftsmanCostProps,
  improvements: ReturnType<typeof selectImprovementsEntities>,
  propertyIdentification: ReturnType<typeof selectPropertyIdentificationEntities>,
  id: string,
  type: 'totalCost' | 'zipOnly',
) => {
  const costOutput = calculateTotalBasementAreaCost(
    costProps,
    improvements,
    propertyIdentification,
    id,
  );

  if (!costOutput) return AloftPrice.zero;

  return type === 'totalCost' ? costOutput.totalCost : costOutput.multiplierBreakdown[type];
};

const getTotalBasementAreaCost = (
  costProps: CraftsmanCostProps,
  improvements: ReturnType<typeof selectImprovementsEntities>,
  propertyIdentification: ReturnType<typeof selectPropertyIdentificationEntities>,
  id: string,
) =>
  getTotalOrBreakdownBasementAreaCost(
    costProps,
    improvements,
    propertyIdentification,
    id,
    'totalCost',
  );

export const selectTotalBasementAreaCost = createPriceSelector(
  [
    selectCostProps,
    selectImprovementsEntities,
    selectPropertyIdentificationEntities,
    (state, id: string) => id,
  ],
  getTotalBasementAreaCost,
);

export const selectZipOnlyBasementAreaCost = createPriceSelector(
  [
    selectCostProps,
    selectImprovementsEntities,
    selectPropertyIdentificationEntities,
    (state, id: string) => id,
  ],
  (costProps, improvements, propertyIdentification, id) =>
    getTotalOrBreakdownBasementAreaCost(
      costProps,
      improvements,
      propertyIdentification,
      id,
      'zipOnly',
    ),
);

export const selectTotalBasementAreaCosts = createSelector(
  [
    selectCostProps,
    selectImprovementsEntities,
    selectPropertyIdentificationEntities,
    selectImprovementEntityIds,
  ],
  (costProps, improvements, propertyIdentification, improvementEntityIds) => improvementEntityIds.reduce((acc: Record<string, AloftPrice>, propID) => {
      const cost = getTotalBasementAreaCost(
        costProps,
        improvements,
        propertyIdentification,
        propID,
      );
      if (cost) acc[propID] = cost;
      return acc;
    }, {}),
);

export const selectSubjectTotalBasementAreaCostOutput = createSelector(
  [selectCostProps, selectImprovementsEntities, selectPropertyIdentificationEntities],
  (costProps, improvements, propertyIdentification) =>
    calculateTotalBasementAreaCost(costProps, improvements, propertyIdentification, 'subject'),
);

export const selectSubjectArvTotalBasementAreaCostOutput = createSelector(
  [selectCostProps, selectImprovementsEntities, selectPropertyIdentificationEntities],
  (costProps, improvements, propertyIdentification) =>
    calculateTotalBasementAreaCost(costProps, improvements, propertyIdentification, 'subjectArv'),
);

export const selectBasementTotalCostDisplay = createSelector(
  [selectTotalBasementAreaCost],
  (basementTotalCost) => basementTotalCost?.toString(true),
);
